/**
 * This Service file contains CloudSyncService, which communicates with purchaseAPI for the following purposes:
 *   1. As a plan is edited, upsert the encrypted plan and related data for analytics to purchaseAPI
 *     1a. retries queued upserts once the app is online
 *   2. When sync runs, pull the most recent plan information from purchaseAPI given an AFP for their upcoming appointments
 * */
import type { useRequestQueue } from '@/mixins/requestQueue'
import { useCloudSyncDocumentService } from '@/services/CloudSync/cloudSyncDocumentService'
import { useCloudSyncPlanService } from '@/services/CloudSync/cloudSyncPlanService'
import { useCloudSyncSessionService } from '@/services/CloudSync/cloudSyncSessionService'
import { Feature, useFeatureService } from '@/services/featureService'
import type { UploadStatusTabulation } from '@/types'
import { defineStore } from 'pinia'
import { computed, type ComputedRef, ref, watch } from 'vue'

export interface CloudSyncInterface<ValidateArg> {
	debouncePeriod: number
	upsertQueue: ReturnType<typeof useRequestQueue>
	registerForUpsert: (id: string) => Promise<void>
	validateForUpsert: (item: ValidateArg) => boolean
	retrySync: () => Promise<void>
	clearStorage: () => Promise<void>
	purge: () => Promise<void>
	flushPendingUpserts: () => Promise<void>
	pendingDebounces: ComputedRef<number>
	serviceName: string
	getRelatedPlanIdsForPendingUpserts: () => Promise<string[]>
}

const debug = import.meta.env['VITE_CLOUD_SYNC_DEBUG'] === 'true'

const browserDocument = document

export const useCloudSyncService = defineStore('cloudSync', () => {
	const featureService = useFeatureService()
	const cloudSyncIsOn = featureService.isEnabled(Feature.CloudSync)

	const debugLog = (log: string) => {
		if (debug && cloudSyncIsOn && import.meta.env.MODE !== 'test') {
			console.log('[CloudSyncDebug] ' + log)
		}
	}

	const plan = useCloudSyncPlanService(debugLog)
	const session = useCloudSyncSessionService(debugLog)
	const document = useCloudSyncDocumentService(debugLog)

	const services = [plan, session, document]

	/**
	 * Retry all failed/queued upsert requests, called whenever the app is online
	 */
	const retrySync = async () => {
		for (const item of services) {
			await item.retrySync()
		}
		await updateTabulation()
	}

	const purge = async () => {
		for (const service of services) {
			await service.purge()
		}
	}

	browserDocument.addEventListener('visibilitychange', () => {
		if (browserDocument.visibilityState == 'hidden') {
			flushPendingUpserts().catch()
		}
	})

	const flushPendingUpserts = async () => {
		debugLog('Starting flush')
		try {
			await Promise.all(
				services.map((cloudSyncService) => {
					return cloudSyncService.flushPendingUpserts()
				})
			)
			debugLog('flush successful')
		} catch (e) {
			debugLog('flush failed')
		}
	}

	const pendingDebounces = ref({ plan: 0, session: 0, document: 0 })

	watch(
		() => {
			return {
				plan: plan.pendingDebounces?.value,
				session: session.pendingDebounces?.value,
				document: document.pendingDebounces?.value,
			}
		},
		(newPendingDebounceCounts) => {
			pendingDebounces.value = newPendingDebounceCounts
		},
		{ deep: true, immediate: true }
	)

	const hasPendingDebounces = computed(() => {
		return Object.values(pendingDebounces.value).some((item) => item !== 0)
	})

	/**
	 * We include all the statuses here, including completed so that it is easier
	 * to debug and see what is happening.
	 */
	const requestStatuses = ref<Record<string, UploadStatusTabulation>>()

	const pendingPlanIds = ref<string[]>([])

	// triggers the subServices to update their pendingSyncs tabulation
	const updateTabulation = async () => {
		for (const cloudSyncService of services) {
			await cloudSyncService.upsertQueue.tabulatePendingUpserts()
		}
	}

	/**
	 * This looks directly at the queues instead of using the request statuses
	 * ref above, so that each queue can dictate what is 'pending'.
	 * Not all statuses are 'pending'.
	 */
	const pendingRequests = computed(() => {
		return services.reduce(
			(acc, item) => acc + item.upsertQueue.pendingRequests.value,
			0
		)
	})

	const hasInProgressUpserts = computed(() => {
		const inProgressCount = services.reduce(
			(acc, item) => acc + item.upsertQueue.inProgressRequests.value,
			0
		)
		return inProgressCount > 0
	})

	const hasPendingUpserts = computed(() => {
		if (pendingRequests.value === undefined) {
			// We're defaulting to true so they can't log out before pending upserts is calculated
			return true
		}
		return pendingRequests.value > 0
	})

	const getRelatedPlanIdsForPendingUpserts = async (): Promise<string[]> => {
		let ids: string[] = []
		for (const service of services) {
			const list = await service.getRelatedPlanIdsForPendingUpserts()
			ids.push(...list)
		}
		return Array.from(new Set(ids))
	}

	const hasPendingCloudSyncs = () => {
		return (
			cloudSyncIsOn && (hasPendingUpserts.value || hasPendingDebounces.value)
		)
	}

	const hasInProgressSyncs = () => {
		return cloudSyncIsOn && hasInProgressUpserts.value
	}

	watch(
		() => {
			return {
				plan: plan.upsertQueue?.requestStatuses?.value,
				session: session.upsertQueue?.requestStatuses?.value,
				document: document.upsertQueue?.requestStatuses?.value,
			}
		},
		async (newRequestStatuses) => {
			requestStatuses.value = newRequestStatuses
			pendingPlanIds.value = await getRelatedPlanIdsForPendingUpserts()
		},
		{ deep: true, immediate: true }
	)

	return {
		plan,
		session,
		document,
		hasPendingCloudSyncs,
		retrySync,
		purge,
		pendingPlanIds,
		flushPendingUpserts,
		pendingDebounces,
		hasPendingDebounces,
		hasPendingUpserts,
		updateTabulation,
		requestStatuses,
		pendingRequests,
		getRelatedPlanIdsForPendingUpserts,
		hasInProgressSyncs,
	}
})
